'use client'

/**
 * TeamRoster — the one "Team" table every internal Ordo app renders, HQ's
 * Users list included: the HQ staff roster with each person's team, personas,
 * how they sign in and when they were last seen.
 *
 * Team lists HQ users only (Ordo staff from Multipass). Customers live in
 * Customers and ambassadors in Schedule; they are separate tables and never
 * belong here.
 *
 * Props-driven: each host does its own fetching (apps via `@ordo/auth`'s
 * `createTeamRosterRoute`, HQ via its admin users API) and passes the rows in.
 * Read-only by default — rows link out to the person's HQ page. Hosts opt in
 * to more: HQ adds a row menu, persona / app filter chips, Export and "/" to
 * search; Tasker adds an Owns column and row actions. There are no row
 * checkboxes or bulk bar: actions are per row.
 *
 * The table scrolls sideways on phones (`TableContainer`). While `isLoading`
 * and no data has arrived, a skeleton renders the same rows cell-for-cell.
 */

import {
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ExternalLink,
  KeyRound,
  Search,
  SearchX,
  Shield,
  Tags,
  UserCog,
  Users,
} from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { formatRelative, initials, isStale, STALE_AFTER_DAYS } from '../person-format'
import { sameEnvOrigin } from '../same-env-origin'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import { Badge } from './badge'
import { buttonVariants } from './button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './dropdown-menu'
import { EmptyState } from './empty-state'
import { Input } from './input'
import { Kbd } from './kbd'
import { Skeleton } from './skeleton'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from './table'
import { ViewToggle } from './view-toggle'

export type TeamRosterRole = 'VIEWER' | 'EDITOR' | 'ADMIN'

export interface TeamRosterTeam {
  id: string
  name: string
  slug: string
  /** Multipass team palette entry: `primary` is the dot. */
  color: { name: string; light: string; primary: string; dark: string }
}

/** Structural twin of `@ordo/auth`'s `TeamRosterMember` (no package dependency). */
export interface TeamRosterPerson {
  id: string
  email: string
  name: string | null
  picture: string | null
  /** False = archived (deactivated in HQ). */
  active: boolean
  subTeam: TeamRosterTeam | null
  /** `grantsAdmin` personas get the shield badge. */
  personas: { slug: string; name: string; grantsAdmin?: boolean }[]
  /** Effective role on this app; null = no access. Only read when the Role column is on. */
  role?: TeamRosterRole | null
  /** Own HQ admin flag; shown as an Admin badge when no persona is held. */
  isAdmin?: boolean
  /** ISO timestamp of the last sign-in; null = never. Absent = unknown (older multipass). */
  lastLoginAt?: string | null
  /** A Google Workspace account is linked. Absent = unknown (older multipass). */
  hasGoogleIdentity?: boolean
}

export type TeamRosterStatus = 'active' | 'archived'

/** An app-specific column, rendered after Last seen (e.g. Tasker's Owns). */
export interface TeamRosterColumn {
  id: string
  header: React.ReactNode
  cell: (person: TeamRosterPerson) => React.ReactNode
  className?: string
}

/** Renders a row's links. Defaults to `<a>`; HQ passes its router link. */
export type TeamRosterLink = React.ComponentType<{ href: string; className?: string; children: React.ReactNode }>

export interface TeamRosterProps {
  /** The roster. `undefined` while the first load is in flight. */
  members: TeamRosterPerson[] | undefined
  /** Render the skeleton while true and `members` has not arrived. */
  isLoading?: boolean
  /** Load failure message; replaces the rows. */
  error?: string | null
  /** This app's display name, used in the role column ("Role in Factory"). */
  appName?: string
  /** Show the per-app Role column. Default true; HQ turns it off. */
  showRole?: boolean
  /** Per-app names for the roles (e.g. Factory calls EDITOR "Operator"). */
  roleLabels?: Partial<Record<TeamRosterRole, string>>
  /**
   * HQ origin. Defaults to production HQ retargeted onto the page's own
   * deployment ring (`sameEnvOrigin`), which is right for prod, preview envs
   * and local dev.
   */
  hqUrl?: string
  /**
   * Where a person's name links. Default: their HQ page, in a new tab. A host
   * that IS HQ passes its own route plus `linkComponent`.
   */
  userHref?: (person: TeamRosterPerson) => string
  /** Where a team name links; omitted = plain text. */
  teamHref?: (team: TeamRosterTeam) => string
  /** Link renderer for `userHref` / `teamHref`. Default `<a target="_blank">`. */
  linkComponent?: TeamRosterLink
  /** Show the Active / Archived toggle. Default true; HQ has its own Archived tab. */
  showStatusFilter?: boolean
  /** Initial Active / Archived filter. Default `active`. */
  defaultStatus?: TeamRosterStatus
  /** Show the "Manage in HQ" menu (people, teams, personas) and footnote. Default true; off inside HQ. */
  manageInHq?: boolean
  /** Where a persona badge links (HQ links to its Personas page); omitted = plain badge. */
  personaHref?: (persona: { slug: string; name: string }) => string
  /** Extra chips after the team and inactivity chips (HQ's "+ Persona"). Narrow `members` yourself. */
  filterChips?: React.ReactNode
  /** True while `filterChips` narrow `members`, so an empty table reads "No people match". */
  hostFiltered?: boolean
  /** Extra columns after Last seen. */
  columns?: TeamRosterColumn[]
  /** A trailing per-row cell (e.g. an actions menu). */
  rowActions?: (person: TeamRosterPerson) => React.ReactNode
  /** Extra toolbar content, right-aligned. A function gets the rows on screen (e.g. Export). */
  toolbarActions?: React.ReactNode | ((visible: TeamRosterPerson[]) => React.ReactNode)
  searchPlaceholder?: string
  /** Ref to the search input, for a host keyboard shortcut. */
  searchRef?: React.Ref<HTMLInputElement>
  /** That shortcut's key, shown as a hint in the empty search box (e.g. "/"). */
  searchShortcut?: string
  className?: string
}

const DEFAULT_HQ_URL = 'https://hq.ordo.com'

const DEFAULT_ROLE_LABELS: Record<TeamRosterRole, string> = {
  ADMIN: 'Admin',
  EDITOR: 'Member',
  // VIEWER is retired; a legacy value shows as Member.
  VIEWER: 'Member',
}

const ROLE_VARIANT: Record<TeamRosterRole, 'default' | 'secondary' | 'outline'> = {
  ADMIN: 'default',
  EDITOR: 'secondary',
  VIEWER: 'secondary',
}

/** Team filter value for "everyone". */
export const ALL_TEAMS = '__all__'
/** Team filter value for people with no sub-team. */
export const NO_TEAM = '__none__'

/** HQ's admin page for one person. */
export function hqUserUrl(hqUrl: string, id: string): string {
  return `${hqUrl.replace(/\/+$/, '')}/admin/users/${encodeURIComponent(id)}`
}

export function displayName(person: Pick<TeamRosterPerson, 'name' | 'email'>): string {
  return person.name?.trim() || person.email.split('@')[0] || person.email
}

/**
 * Status + free-text + team + inactivity filter. Search matches name, email,
 * team and persona names, case-insensitively. Pure so hosts and tests share
 * the rule.
 */
export function filterTeamRoster(
  members: readonly TeamRosterPerson[],
  opts: { status?: TeamRosterStatus; query?: string; team?: string; staleOnly?: boolean; now?: Date },
): TeamRosterPerson[] {
  const needle = opts.query?.trim().toLowerCase() ?? ''
  const team = opts.team ?? ALL_TEAMS
  return members.filter((m) => {
    if (opts.status && m.active !== (opts.status === 'active')) {
      return false
    }
    if (team !== ALL_TEAMS && (m.subTeam?.id ?? NO_TEAM) !== team) {
      return false
    }
    if (opts.staleOnly && !isStale(m.lastLoginAt, opts.now)) {
      return false
    }
    if (!needle) {
      return true
    }
    const haystack = [m.name ?? '', m.email, m.subTeam?.name ?? '', ...m.personas.map((p) => p.name)]
    return haystack.some((s) => s.toLowerCase().includes(needle))
  })
}

export type TeamRosterSortKey = 'name' | 'lastLoginAt'
export type TeamRosterSortDir = 'asc' | 'desc'

/** Name A–Z / Z–A, or by last sign-in with never-signed-in always last. */
export function sortTeamRoster(
  members: readonly TeamRosterPerson[],
  key: TeamRosterSortKey,
  dir: TeamRosterSortDir,
): TeamRosterPerson[] {
  const factor = dir === 'asc' ? 1 : -1
  return [...members].sort((a, b) => {
    if (key === 'name') {
      return factor * displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' })
    }
    const x = a.lastLoginAt ?? null
    const y = b.lastLoginAt ?? null
    if (x === y) {
      return 0
    }
    if (x === null) {
      return 1
    }
    if (y === null) {
      return -1
    }
    return factor * (x < y ? -1 : 1)
  })
}

/** One chip per team present (with counts), then "No team" when anyone lacks one. */
export function teamRosterTeamCounts(
  members: readonly TeamRosterPerson[],
): { team: TeamRosterTeam | null; count: number }[] {
  const byId = new Map<string, { team: TeamRosterTeam | null; count: number }>()
  for (const m of members) {
    const key = m.subTeam?.id ?? NO_TEAM
    const entry = byId.get(key) ?? { team: m.subTeam, count: 0 }
    entry.count += 1
    byId.set(key, entry)
  }
  const teams = [...byId.values()].filter((e) => e.team !== null)
  teams.sort((a, b) => a.team!.name.localeCompare(b.team!.name, undefined, { sensitivity: 'base' }))
  const none = byId.get(NO_TEAM)
  return none ? [...teams, none] : teams
}

const ExternalAnchor: TeamRosterLink = ({ href, className, children }) => (
  <a href={href} target="_blank" rel="noreferrer" className={className}>
    {children}
  </a>
)

function SortHeader({
  label,
  sortKey,
  active,
  dir,
  onSort,
}: {
  label: string
  sortKey: TeamRosterSortKey
  active: boolean
  dir: TeamRosterSortDir
  onSort: (key: TeamRosterSortKey) => void
}) {
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={cn(
        'group inline-flex items-center gap-1 text-left hover:text-foreground',
        active && 'text-foreground',
      )}>
      {label}
      <Icon className={cn('h-3 w-3', !active && 'opacity-40 group-hover:opacity-100')} />
    </button>
  )
}

/** Toolbar filter pill — pressed state reads as a filled chip. Exported for host chips (`filterChips`). */
export function TeamRosterFilterChip({
  pressed,
  onClick,
  children,
  tone = 'neutral',
}: {
  pressed: boolean
  onClick: () => void
  children: React.ReactNode
  tone?: 'neutral' | 'warn'
}) {
  const look =
    tone === 'warn'
      ? pressed
        ? 'border-amber-700 bg-amber-700 text-white'
        : 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200'
      : pressed
        ? 'border-foreground bg-foreground text-background'
        : 'border-border bg-background text-foreground hover:bg-muted'
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium tabular-nums transition-colors',
        look,
      )}>
      {children}
    </button>
  )
}

function TeamDot({ team }: { team: TeamRosterTeam }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block h-2 w-2 shrink-0 rounded-full', !team.color.primary && 'border border-border')}
      style={team.color.primary ? { backgroundColor: team.color.primary } : undefined}
    />
  )
}

function PersonaBadges({
  person,
  personaHref,
  Link,
}: {
  person: TeamRosterPerson
  personaHref?: TeamRosterProps['personaHref']
  Link: TeamRosterLink
}) {
  if (person.personas.length > 0) {
    return (
      <span className="flex flex-wrap gap-1">
        {person.personas.map((p) => {
          const badge = (
            <Badge
              variant={p.grantsAdmin ? 'default' : 'secondary'}
              className={cn('gap-1 font-normal', personaHref && 'transition-opacity hover:opacity-80')}
              title={p.slug}>
              {p.grantsAdmin && <Shield className="h-3 w-3" />}
              {p.name}
            </Badge>
          )
          return personaHref ? (
            <Link key={p.slug} href={personaHref(p)} className="inline-flex">
              {badge}
            </Link>
          ) : (
            <React.Fragment key={p.slug}>{badge}</React.Fragment>
          )
        })}
      </span>
    )
  }
  if (person.isAdmin) {
    return (
      <Badge variant="default" className="gap-1">
        <Shield className="h-3 w-3" />
        Admin
      </Badge>
    )
  }
  return <span className="text-xs text-muted-foreground">Team defaults</span>
}

function SignIn({ person }: { person: TeamRosterPerson }) {
  if (person.hasGoogleIdentity === undefined) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  return person.hasGoogleIdentity ? (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground text-foreground">
      <KeyRound className="h-3 w-3" /> Google
    </span>
  ) : (
    <span className="text-xs text-muted-foreground">Invite / password</span>
  )
}

function LastSeen({ person }: { person: TeamRosterPerson }) {
  if (person.lastLoginAt === undefined) {
    return <TableCell className="text-xs text-muted-foreground">—</TableCell>
  }
  const stale = isStale(person.lastLoginAt)
  return (
    <TableCell
      className={cn(
        'tabular-nums',
        stale ? 'text-xs font-medium text-amber-700 dark:text-amber-400' : 'text-xs text-muted-foreground',
      )}
      title={person.lastLoginAt ? new Date(person.lastLoginAt).toLocaleString() : undefined}>
      {formatRelative(person.lastLoginAt)}
    </TableCell>
  )
}

interface ColumnFlags {
  showRole: boolean
  extra: TeamRosterColumn[]
  trailing: boolean
}

function columnCount(f: ColumnFlags): number {
  return 5 + (f.showRole ? 1 : 0) + f.extra.length + (f.trailing ? 1 : 0)
}

/** Skeleton rows matching the real rows cell-for-cell. */
export function TeamRosterSkeleton({
  rows = 6,
  showRole = true,
  extraColumns = 0,
  trailing = true,
}: {
  rows?: number
  showRole?: boolean
  extraColumns?: number
  trailing?: boolean
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i} data-testid="team-roster-skeleton">
          <TableCell>
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-44" />
              </div>
            </div>
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          {showRole && (
            <TableCell>
              <Skeleton className="h-5 w-14 rounded-full" />
            </TableCell>
          )}
          <TableCell>
            <Skeleton className="h-5 w-24 rounded-full" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-3 w-16" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-3 w-20" />
          </TableCell>
          {Array.from({ length: extraColumns }).map((__, c) => (
            <TableCell key={c}>
              <Skeleton className="h-4 w-24" />
            </TableCell>
          ))}
          {trailing && (
            <TableCell className="w-12">
              <Skeleton className="h-8 w-8" />
            </TableCell>
          )}
        </TableRow>
      ))}
    </>
  )
}

export function TeamRoster({
  members,
  isLoading = false,
  error = null,
  appName,
  showRole = true,
  roleLabels,
  hqUrl,
  userHref,
  teamHref,
  linkComponent,
  showStatusFilter = true,
  defaultStatus = 'active',
  manageInHq = true,
  personaHref,
  filterChips,
  hostFiltered = false,
  columns = [],
  rowActions,
  toolbarActions,
  searchPlaceholder = 'Search the team',
  searchRef,
  searchShortcut,
  className,
}: TeamRosterProps) {
  const [status, setStatus] = React.useState<TeamRosterStatus>(defaultStatus)
  const [query, setQuery] = React.useState('')
  const [team, setTeam] = React.useState(ALL_TEAMS)
  const [staleOnly, setStaleOnly] = React.useState(false)
  const [sortKey, setSortKey] = React.useState<TeamRosterSortKey>('name')
  const [sortDir, setSortDir] = React.useState<TeamRosterSortDir>('asc')

  const resolvedHqUrl = (hqUrl ?? sameEnvOrigin(DEFAULT_HQ_URL)).replace(/\/+$/, '')
  const labels = { ...DEFAULT_ROLE_LABELS, ...roleLabels }
  const Link = linkComponent ?? ExternalAnchor
  const hrefFor = userHref ?? ((p: TeamRosterPerson) => hqUserUrl(resolvedHqUrl, p.id))

  const all = members ?? []
  const activeCount = all.filter((m) => m.active).length
  const archivedCount = all.length - activeCount
  // Chips count the rows the search and status leave, so a chip never offers
  // people the table can't show.
  const base = filterTeamRoster(all, { status: showStatusFilter ? status : undefined, query })
  const teamCounts = teamRosterTeamCounts(base)
  const staleCount = base.filter((m) => isStale(m.lastLoginAt)).length
  const visible = sortTeamRoster(filterTeamRoster(base, { team, staleOnly }), sortKey, sortDir)
  const showSkeleton = isLoading && members === undefined

  const onSort = (key: TeamRosterSortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir(key === 'lastLoginAt' ? 'desc' : 'asc')
    }
  }

  const flags: ColumnFlags = {
    showRole,
    extra: columns,
    trailing: !!rowActions || manageInHq,
  }
  const span = columnCount(flags)
  let body: React.ReactNode
  if (error) {
    body = (
      <TableRow>
        <TableCell colSpan={span}>
          <EmptyState
            variant="plain"
            padding="md"
            icon={<Users />}
            title="Couldn't load the team"
            description={error}
          />
        </TableCell>
      </TableRow>
    )
  } else if (showSkeleton) {
    body = <TeamRosterSkeleton showRole={showRole} extraColumns={columns.length} trailing={flags.trailing} />
  } else if (visible.length === 0) {
    const filtered = hostFiltered || query.trim().length > 0 || team !== ALL_TEAMS || staleOnly
    body = (
      <TableRow>
        <TableCell colSpan={span}>
          {filtered ? (
            <EmptyState
              variant="plain"
              padding="md"
              icon={<SearchX />}
              title="No people match"
              description="Try a different name, email, team or persona, or clear the filters."
            />
          ) : status === 'archived' && showStatusFilter ? (
            <EmptyState
              variant="plain"
              padding="md"
              icon={<Archive />}
              title="No archived people"
              description="People archived in HQ will appear here."
            />
          ) : (
            <EmptyState
              variant="plain"
              padding="md"
              icon={<Users />}
              title="No team members"
              description="Ordo staff added in HQ will appear here."
            />
          )}
        </TableCell>
      </TableRow>
    )
  } else {
    body = visible.map((person) => {
      const name = displayName(person)
      return (
        <TableRow key={person.id} data-testid="team-roster-row">
          <TableCell>
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8">
                {person.picture ? <AvatarImage src={person.picture} alt="" /> : null}
                <AvatarFallback className="text-xs font-semibold">{initials(person.name, person.email)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Link href={hrefFor(person)} className="truncate font-medium text-foreground hover:underline">
                    {name}
                  </Link>
                  {!person.active && <Badge variant="warning">Archived</Badge>}
                </div>
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">{person.email}</span>
              </div>
            </div>
          </TableCell>
          <TableCell>
            {person.subTeam ? (
              teamHref ? (
                <Link href={teamHref(person.subTeam)} className="inline-flex items-center gap-2 hover:underline">
                  <TeamDot team={person.subTeam} />
                  {person.subTeam.name}
                </Link>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <TeamDot team={person.subTeam} />
                  {person.subTeam.name}
                </span>
              )
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </TableCell>
          {showRole && (
            <TableCell>
              {person.role ? (
                <Badge variant={ROLE_VARIANT[person.role]}>{labels[person.role]}</Badge>
              ) : (
                <span className="text-xs text-muted-foreground">No access</span>
              )}
            </TableCell>
          )}
          <TableCell>
            <PersonaBadges person={person} personaHref={personaHref} Link={Link} />
          </TableCell>
          <TableCell>
            <SignIn person={person} />
          </TableCell>
          <LastSeen person={person} />
          {columns.map((c) => (
            <TableCell key={c.id} className={c.className}>
              {c.cell(person)}
            </TableCell>
          ))}
          {flags.trailing && (
            <TableCell className="w-12 text-right">
              {rowActions ? (
                rowActions(person)
              ) : (
                <a
                  href={hqUserUrl(resolvedHqUrl, person.id)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Manage ${name} in HQ`}
                  className={cn(
                    buttonVariants({ variant: 'ghost', size: 'icon' }),
                    'h-8 w-8 text-muted-foreground hover:text-foreground',
                  )}>
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </TableCell>
          )}
        </TableRow>
      )
    })
  }

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative w-full max-w-sm">
            <span className="sr-only">Search the team</span>
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-keyshortcuts={searchShortcut}
              className={cn('pl-9', searchShortcut && 'pr-10')}
            />
            {searchShortcut && !query && (
              <Kbd aria-hidden className="absolute right-3 top-1/2 -translate-y-1/2">
                {searchShortcut}
              </Kbd>
            )}
          </label>
          {(showStatusFilter || manageInHq || toolbarActions) && (
            <div className="flex flex-wrap items-center gap-2 md:ml-auto">
              {showStatusFilter && (
                <ViewToggle
                  views={[
                    { id: 'active', label: members ? `Active (${activeCount})` : 'Active', icon: Users },
                    { id: 'archived', label: members ? `Archived (${archivedCount})` : 'Archived', icon: Archive },
                  ]}
                  activeView={status}
                  onChange={(v) => {
                    setStatus(v === 'archived' ? 'archived' : 'active')
                    setTeam(ALL_TEAMS)
                    setStaleOnly(false)
                  }}
                />
              )}
              {typeof toolbarActions === 'function' ? toolbarActions(visible) : toolbarActions}
              {manageInHq && (
                <DropdownMenu>
                  <DropdownMenuTrigger className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}>
                    Manage in HQ
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {(
                      [
                        { path: 'users', label: 'People', icon: Users },
                        { path: 'teams', label: 'Teams', icon: UserCog },
                        { path: 'personas', label: 'Personas', icon: Tags },
                      ] as const
                    ).map((item) => (
                      <DropdownMenuItem key={item.path} asChild className="cursor-pointer">
                        <a href={`${resolvedHqUrl}/admin/${item.path}`} target="_blank" rel="noreferrer">
                          <item.icon className="h-4 w-4" />
                          <span className="flex-1">{item.label}</span>
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                        </a>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>

        {members && (members.length > 0 || hostFiltered) && (
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by team">
            <TeamRosterFilterChip pressed={team === ALL_TEAMS} onClick={() => setTeam(ALL_TEAMS)}>
              All <span className="opacity-70">{base.length}</span>
            </TeamRosterFilterChip>
            {teamCounts.map(({ team: t, count }) => {
              const id = t?.id ?? NO_TEAM
              return (
                <TeamRosterFilterChip key={id} pressed={team === id} onClick={() => setTeam(id)}>
                  {t ? <TeamDot team={t} /> : null}
                  {t?.name ?? 'No team'} <span className="opacity-70">{count}</span>
                </TeamRosterFilterChip>
              )
            })}
            {staleCount > 0 && (
              <TeamRosterFilterChip tone="warn" pressed={staleOnly} onClick={() => setStaleOnly((v) => !v)}>
                Inactive {STALE_AFTER_DAYS}+ days · {staleCount}
              </TeamRosterFilterChip>
            )}
            {filterChips}
          </div>
        )}
      </div>

      <TableContainer>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">
                <SortHeader label="Name" sortKey="name" active={sortKey === 'name'} dir={sortDir} onSort={onSort} />
              </TableHead>
              <TableHead className="text-xs">Team</TableHead>
              {showRole && <TableHead className="text-xs">{appName ? `Role in ${appName}` : 'Role'}</TableHead>}
              <TableHead className="text-xs">Personas</TableHead>
              <TableHead className="text-xs">Sign-in</TableHead>
              <TableHead className="text-xs">
                <SortHeader
                  label="Last seen"
                  sortKey="lastLoginAt"
                  active={sortKey === 'lastLoginAt'}
                  dir={sortDir}
                  onSort={onSort}
                />
              </TableHead>
              {columns.map((c) => (
                <TableHead key={c.id} className={cn('text-xs', c.className)}>
                  {c.header}
                </TableHead>
              ))}
              {flags.trailing && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>{body}</TableBody>
        </Table>
      </TableContainer>

      <p className="text-xs text-muted-foreground tabular-nums">
        {members ? `Showing ${visible.length.toLocaleString()} of ${base.length.toLocaleString()}` : null}
        {manageInHq ? `${members ? ' · ' : ''}Access is managed in HQ.` : null}
      </p>
    </div>
  )
}
